import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
  addNewWordsToGlobal,
  addVariousWordsToGlobal,
  addVariousWordsToGlobalUndo,
  addWordsToGroup,
  addWordsToGroupUndo,
  assignGroupToArticle,
  assignGroupToArticleUndo,
  deleteGroup,
  deleteGroupUndo,
  deleteRestrictedWords,
  deleteWords,
  deleteWordsFromGlobal,
  deleteWordsFromGroup,
  deleteWordUndo,
  fetchGroupArticles,
  fetchRestrictedGroups,
  fetchRestrictedWord,
  fetchRestrictedWordsInitData,
  makeWordsGlobal,
  makeWordsGlobalUndo,
  refreshRestrictedWordsData,
  updateRestrictedGroup,
  updateRestrictedGroupUndo,
  updateRestrictedWord,
  updateWordUndo
} from './restrictedActions';
import {
  addGlobalRestrictedWordsRequest,
  addRestrictedWordsToGroupRequest,
  addRestrictionGroupsRequest,
  assignGroupToArticleRequest,
  deleteGlobalRestrictedWordsRequest,
  deleteGroupRequest,
  deleteRestrictedWordsFromGroupRequest,
  deleteRestrictedWordsRequest,
  fetchGlobalRestrictedWordsRequest,
  fetchGroupArticlesRequest,
  fetchRestrictedGroupsRequest,
  fetchRestrictedWordRequest,
  fetchRestrictedWordsByGroupRequest,
  fetchRestrictionGroupsRequest,
  fetchSearchRestrictedWordsRequest,
  makesRestrictedWordsGlobalRequest,
  updateRestrictedGroupRequest,
  updateRestrictedWordRequest,
  updateWordPolicyRequest
} from './restrictedWordsApi';
import {
  addRestrictedWords,
  changeSelectedGroup,
  closeGroupSettings,
  closeWordSettings,
  removeRestrictedWords,
  setArticles,
  setInitData,
  setRestrictedGroups,
  setRestrictedWords,
  setRestrictedWordsIsLoading,
  setRestrictionGroups,
  setSearchWords,
  setWordSettings,
  updateWordPolicy
} from './restrictedWordsSlice';
import {
  isGlobalGroup,
  localCompare,
  parseExceptions,
  parseGroups,
  parseWords,
  parseWordSettings,
  updateCreatedRestcitionGroupsIdForUndo
} from './restrictedWordsUtils';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  AddRestrictedWordsResponse,
  AddRestrictedWordsToGroupPayload,
  AddRestrictionGroupsResponse,
  AddVariousWordsToGlobalPayload,
  AddVariousWordsToGlobalUndoPayload,
  AddWordsToGroupUndoPayload,
  AssignGroupToArticlePayload,
  DeleteGlobalRestrictedWordsPayload,
  DeleteGroupPayload,
  DeleteRestrictedWordsFromGroupPayload,
  DeleteRestrictedWordsPayload,
  DeleteWordsPayload,
  DeleteWordUndoPayload,
  FetchRestrictedGroupsResponse,
  FetchRestrictedWordPayload,
  FetchRestrictedWordsResponse,
  MakeWordsGlobalPayload,
  MakeWordsGlobalUndoPayload,
  ResetGroupsResponse,
  RestrictedGroup,
  RestrictedWordBase,
  RestrictedWordPolicy,
  UpdateGroupPayload,
  UpdateRestrictedWordPayload,
  UpdateWordPolicyPayload,
  UpdateWordUndoPayload
} from './restrictedWordsTypes';
import { DEFAULT_WORD_POLICY, UNDO_TOAST_MESSAGE_PAYLOAD } from './restrictedWordsConstants';
import { updateArticlesGroups } from '../articles/reducers/loadArticlesSlice';
import { showToast } from '../../../../components/toast/toastSlice';
import { ToastType } from '../../../../components/toast/toastTypes';

function* fetchRestrictedWordsInitDataSaga(action: PayloadAction<number>) {
  const selectedGroupId = action.payload;

  yield put(setRestrictedWordsIsLoading(true));

  try {
    const fetchWordsCall = isGlobalGroup(selectedGroupId)
      ? call(fetchGlobalRestrictedWordsRequest)
      : call(fetchRestrictedWordsByGroupRequest, selectedGroupId);

    const [wordsResponse, searchResponse, groupsResponse]: [
      FetchRestrictedWordsResponse,
      FetchRestrictedWordsResponse,
      FetchRestrictedGroupsResponse
    ] = yield all([
      fetchWordsCall,
      call(fetchSearchRestrictedWordsRequest),
      call(fetchRestrictedGroupsRequest)
    ]);

    const parsedWords = parseWords(wordsResponse);
    const parsedWordsSorted = [...parsedWords].sort(localCompare);
    const parsedSearch = parseWords(searchResponse);
    const parsedGroups = parseGroups(groupsResponse);

    yield put(
      setInitData({
        words: parsedWordsSorted,
        searchWords: parsedSearch,
        groups: parsedGroups
      })
    );
  } catch (error) {
    yield showError();
  }

  yield put(setRestrictedWordsIsLoading(false));
}

function* fetchRestrictedWordsByGroupSaga(action: PayloadAction<RestrictedGroup>) {
  yield put(setRestrictedWordsIsLoading(true));

  const fetchWordsCall = isGlobalGroup(action.payload)
    ? call(fetchGlobalRestrictedWordsRequest)
    : call(fetchRestrictedWordsByGroupRequest, action.payload.id);

  try {
    const [wordsResponse, searchResponse]: [
      FetchRestrictedWordsResponse,
      FetchRestrictedWordsResponse
    ] = yield all([
      fetchWordsCall,
      // refetch all words to be upto date
      call(fetchSearchRestrictedWordsRequest)
    ]);

    const parsedWords = parseWords(wordsResponse);
    const parsedSearch = parseWords(searchResponse);

    yield put(setRestrictedWords(parsedWords));
    yield put(setSearchWords(parsedSearch));
  } catch (error) {
    yield showError();
  }

  yield put(setRestrictedWordsIsLoading(false));
}

function* updateWordPolicySaga(action: PayloadAction<UpdateWordPolicyPayload>) {
  try {
    yield call(updateWordPolicyRequest, action.payload);
    yield put(
      showToast({
        type: 'warning',
        message: '1 Word was updated'
      })
    );
  } catch (error) {
    yield showError();
  }
}

function showError() {
  return put(
    showToast({
      type: 'error',
      message: 'Something went wrong, please try again'
    })
  );
}

function* addNewWordsToGlobalSaga(action: PayloadAction<Array<string>>) {
  try {
    const response: AddRestrictedWordsResponse = yield call(
      addGlobalRestrictedWordsRequest,
      action.payload
    );

    const parsedWords = parseWords(response);

    yield put(addRestrictedWords(parsedWords));

    yield put(
      showToast({
        type: 'success',
        message: 'New word/s added',
        onAction: dispatch => {
          dispatch(deleteWordsFromGlobal({ words: parsedWords, source: 'undo' }));
        }
      })
    );

    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function* deleteWordsFromGlobalSaga(action: PayloadAction<DeleteGlobalRestrictedWordsPayload>) {
  try {
    const { words, source } = action.payload;

    yield call(deleteGlobalRestrictedWordsRequest, words);
    yield put(removeRestrictedWords(words));

    if (source === 'undo') {
      yield put(showToast(UNDO_TOAST_MESSAGE_PAYLOAD));
    }

    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function* makeWordsGlobalSaga(action: PayloadAction<MakeWordsGlobalPayload>) {
  try {
    const { words, selectedGroupId, isWordSettingsFlow } = action.payload;

    const response: ResetGroupsResponse = yield call(makesRestrictedWordsGlobalRequest, words);

    if (isGlobalGroup(selectedGroupId)) {
      yield put(addRestrictedWords(words));
    }

    if (isWordSettingsFlow) {
      yield put(closeWordSettings());
    }

    yield put(
      showToast({
        type: 'warning',
        message: 'Word/s transferred to a globally applicable list',
        onAction: dispatch => {
          dispatch(
            makeWordsGlobalUndo({ initialWords: words, updatedWords: response, selectedGroupId })
          );
        }
      })
    );

    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function* makeWordsGlobalUndoSaga(action: PayloadAction<MakeWordsGlobalUndoPayload>) {
  const { initialWords, updatedWords, selectedGroupId } = action.payload;

  const calls = getAddRestrictedWordsToGroupCalls(updatedWords);

  try {
    yield all(calls);

    if (isGlobalGroup(selectedGroupId)) {
      yield put(removeRestrictedWords(initialWords));
    } else {
      yield put(addRestrictedWords(initialWords));
    }

    yield put(showToast(UNDO_TOAST_MESSAGE_PAYLOAD));
    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function getAddRestrictedWordsToGroupCalls(updatedWords: ResetGroupsResponse) {
  const updatedWordsMap = updatedWords.reduce<Record<number, Array<RestrictedWordBase>>>(
    (acc, { group_id, word_id, policy }) => {
      const value = { id: word_id, policy };

      if (acc[group_id]) {
        acc[group_id].push(value);
      } else {
        acc[group_id] = [value];
      }
      return acc;
    },
    {}
  );

  return Object.entries(updatedWordsMap).map(([groupId, words]) => {
    // @ts-ignore
    return call(addRestrictedWordsToGroupRequest, groupId, words);
  });
}

function* addVariousWordsToGlobalSaga(action: PayloadAction<AddVariousWordsToGlobalPayload>) {
  try {
    const { newWords, wordsWithGroups } = action.payload;

    const [createdWordsResponse, makeWordsGlobalResponse]: [
      AddRestrictedWordsResponse,
      ResetGroupsResponse
    ] = yield all([
      call(addGlobalRestrictedWordsRequest, newWords),
      call(makesRestrictedWordsGlobalRequest, wordsWithGroups)
    ]);

    const parsedWords = parseWords(createdWordsResponse);

    yield put(addRestrictedWords([...parsedWords, ...wordsWithGroups]));

    yield put(
      showToast({
        type: 'warning',
        message: 'Words created and/or transferred to a globally applicable list',
        onAction: dispatch => {
          dispatch(
            addVariousWordsToGlobalUndo({
              newWords: parsedWords,
              updatedWords: makeWordsGlobalResponse,
              wordsWithGroups
            })
          );
        }
      })
    );

    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function* addVariousWordsToGlobalUndoSaga(
  action: PayloadAction<AddVariousWordsToGlobalUndoPayload>
) {
  const { newWords, updatedWords, wordsWithGroups } = action.payload;

  const addWordsToGroupCalls = getAddRestrictedWordsToGroupCalls(updatedWords);
  const deleteWordsFromGlobalCall = call(deleteGlobalRestrictedWordsRequest, newWords);

  try {
    yield all([...addWordsToGroupCalls, deleteWordsFromGlobalCall]);
    yield put(removeRestrictedWords([...newWords, ...wordsWithGroups]));

    yield put(showToast(UNDO_TOAST_MESSAGE_PAYLOAD));
    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

type AddToGroupToastType = 'without-global' | 'only-global' | 'all';
type ToastInfo = { type: ToastType; message: string };

const addToGroupToastInfoMap: Record<AddToGroupToastType, ToastInfo> = {
  'without-global': {
    type: 'success',
    message: 'New word/s added to the group'
  },
  'only-global': {
    type: 'warning',
    message: 'Word/s transferred to a group from globally applicable list'
  },
  all: {
    type: 'warning',
    message: 'Words created and/or transferred to a group from globally applicable list'
  }
};

function* addWordsToGroupSaga(action: PayloadAction<AddRestrictedWordsToGroupPayload>) {
  try {
    const {
      newWords = [],
      wordsWithGroups = [],
      globalWords = [],
      groupId,
      source
    } = action.payload;

    let toastType: AddToGroupToastType;

    if (globalWords.length === 0) {
      toastType = 'without-global';
    } else {
      toastType = newWords.length === 0 && wordsWithGroups.length === 0 ? 'only-global' : 'all';
    }

    const globalWordsMap = globalWords.reduce<Record<string, RestrictedWordPolicy>>((acc, word) => {
      acc[word.id] = word.policy;
      return acc;
    }, {});

    const existingWords = [...wordsWithGroups, ...globalWords];
    const response: AddRestrictedWordsResponse = yield call(
      addRestrictedWordsToGroupRequest,
      groupId,
      existingWords,
      newWords
    );

    const parsedWords = parseWords(response);

    const updatedExistingWords = existingWords.map(word => ({
      ...word,
      groupsCount: word.groupsCount + 1,
      policy: globalWordsMap[word.id] || DEFAULT_WORD_POLICY
    }));

    const allAddedWords = [...parsedWords, ...updatedExistingWords];

    yield put(addRestrictedWords(allAddedWords));
    if (source === 'undo') {
      yield put(showToast(UNDO_TOAST_MESSAGE_PAYLOAD));
    } else {
      yield put(
        showToast({
          ...addToGroupToastInfoMap[toastType],
          onAction: dispatch => {
            dispatch(
              addWordsToGroupUndo({
                newWords: parsedWords,
                wordsWithGroups,
                globalWords,
                selectedGroupId: groupId
              })
            );
          }
        })
      );
    }

    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function* addWordsToGroupUndoSaga(action: PayloadAction<AddWordsToGroupUndoPayload>) {
  const { newWords, wordsWithGroups, globalWords, selectedGroupId } = action.payload;
  const calls = [];

  if (newWords.length > 0) {
    calls.push(call(deleteRestrictedWordsRequest, newWords));
  }

  if (wordsWithGroups.length > 0) {
    calls.push(call(deleteRestrictedWordsFromGroupRequest, wordsWithGroups, selectedGroupId));
  }

  if (globalWords.length > 0) {
    calls.push(call(makesRestrictedWordsGlobalRequest, globalWords));
  }

  try {
    yield all(calls);
    yield put(removeRestrictedWords([...newWords, ...wordsWithGroups, ...globalWords]));

    yield put(showToast(UNDO_TOAST_MESSAGE_PAYLOAD));
    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function* deleteWordsFromGroupSaga(action: PayloadAction<DeleteRestrictedWordsFromGroupPayload>) {
  try {
    const { words, groupId } = action.payload;

    yield call(deleteRestrictedWordsFromGroupRequest, words, groupId);
    yield put(removeRestrictedWords(words));

    yield put(
      showToast({
        type: 'warning',
        message: 'Word removed from the group',
        onAction: dispatch => {
          dispatch(addWordsToGroup({ wordsWithGroups: [...words], source: 'undo', groupId }));
        }
      })
    );

    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function* deleteWordsSaga(action: PayloadAction<DeleteWordsPayload>) {
  const { words, groupId, isWordSettingsFlow } = action.payload;
  const message =
    words.length === 1 ? `Word <b>“${words[0].value}”</b> deleted` : 'Words removed from the group';

  try {
    yield call(deleteRestrictedWordsRequest, words);
    yield put(removeRestrictedWords(words));

    if (isWordSettingsFlow) {
      yield put(closeWordSettings());
    }

    yield put(
      showToast({
        type: 'warning',
        message,
        onAction: dispatch => {
          const values = words.map(word => word.value);
          dispatch(addWordsToGroup({ newWords: values, source: 'undo', groupId }));
        }
      })
    );

    yield put(refreshRestrictedWordsData());
  } catch (error) {
    yield showError();
  }
}

function* refreshRestrictedWordsDataSaga() {
  try {
    const [searchResponse, groupsResponse]: [
      FetchRestrictedWordsResponse,
      FetchRestrictedGroupsResponse
    ] = yield all([call(fetchSearchRestrictedWordsRequest), call(fetchRestrictedGroupsRequest)]);

    const parsedSearch = parseWords(searchResponse);
    const parsedGroups = parseGroups(groupsResponse);

    yield put(setSearchWords(parsedSearch));
    yield put(setRestrictedGroups(parsedGroups));
  } catch (error) {
    yield showError();
  }
}

function* fetchRestrictedWordSaga(action: PayloadAction<FetchRestrictedWordPayload>) {
  try {
    const { restrictedWordId } = action.payload;
    const [restrictedWordResponse, restrictionGroupsResponse] = yield all([
      call(fetchRestrictedWordRequest, restrictedWordId),
      call(fetchRestrictionGroupsRequest, restrictedWordId)
    ]);
    const word = parseWordSettings(restrictedWordResponse);
    const groups = parseGroups(restrictionGroupsResponse);
    yield put(setWordSettings(word));
    yield put(setRestrictionGroups(groups));
  } catch (error) {
    yield showError();
  }
}

function* updateRestrictedWordSaga(action: PayloadAction<UpdateRestrictedWordPayload>) {
  yield put(setRestrictedWordsIsLoading(true));
  try {
    const {
      id,
      exceptions,
      policy,
      text,
      restrictionGroups,
      restrictedWordForUndo,
      updateWordSucceededCallback,
      selectedGroup
    } = action.payload;
    const parsedExceptions = parseExceptions(exceptions);
    const wordPayload = {
      text,
      policy,
      exceptions: parsedExceptions
    };

    yield call(updateRestrictedWordRequest, wordPayload, id);
    const response: AddRestrictionGroupsResponse = yield call(
      addRestrictionGroupsRequest,
      id,
      restrictionGroups
    );

    const toDeleteUndoData = [...restrictionGroups?.toAdd, ...restrictionGroups?.toCreate];

    const restrictionGroupsToDelete = updateCreatedRestcitionGroupsIdForUndo(
      toDeleteUndoData,
      response
    );

    const undoRestrictionGroupsPayload = {
      toAdd: restrictionGroups.toDelete,
      toDelete: restrictionGroupsToDelete
    };

    const undoWordPayload = {
      text: restrictedWordForUndo?.value,
      policy: restrictedWordForUndo?.policy,
      exceptions: restrictedWordForUndo.exceptions || []
    };

    yield put(fetchRestrictedWord({ restrictedWordId: id }));
    if (updateWordSucceededCallback) {
      updateWordSucceededCallback();
    }
    yield put(changeSelectedGroup(selectedGroup));

    yield put(
      showToast({
        type: 'success',
        message: 'Word updated',
        onAction: dispatch => {
          dispatch(
            updateWordUndo({
              id,
              word: undoWordPayload,
              restrictionGroups: undoRestrictionGroupsPayload,
              selectedGroup
            })
          );
        }
      })
    );

    yield put(fetchRestrictedGroups());
  } catch (error) {
    yield showError();
  }
  yield put(setRestrictedWordsIsLoading(false));
}

function* deleteRestrictedWordsSaga(action: PayloadAction<DeleteRestrictedWordsPayload>) {
  try {
    const {
      words,
      successToastMessage,
      restrictionGroups,
      exceptions,
      selectedGroup
    } = action.payload;
    const wordValue = words[0].value;

    yield call(deleteRestrictedWordsRequest, words);
    yield put(changeSelectedGroup(selectedGroup));
    yield put(closeWordSettings());
    yield put(
      showToast({
        type: 'warning',
        message: successToastMessage || 'Word removed',
        onAction: dispatch => {
          dispatch(
            deleteWordUndo({ word: [wordValue], restrictionGroups, exceptions, selectedGroup })
          );
        }
      })
    );
  } catch (error) {
    yield showError();
  }
}

function* deleteWordUndoSaga(action: PayloadAction<DeleteWordUndoPayload>) {
  const { word, restrictionGroups, exceptions, selectedGroup } = action.payload;

  try {
    const response: AddRestrictedWordsResponse = yield call(addGlobalRestrictedWordsRequest, word);

    const parsedWords = parseWords(response);

    const updateWordPayload = {
      text: parsedWords[0].value,
      policy: parsedWords[0].policy,
      exceptions
    };

    yield all([
      call(addRestrictionGroupsRequest, parsedWords[0].id, restrictionGroups),
      call(updateRestrictedWordRequest, updateWordPayload, parsedWords[0].id)
    ]);

    yield put(changeSelectedGroup(selectedGroup));
    yield put(
      showToast({
        type: 'success',
        message: `Word "${parsedWords[0].value}" restored`
      })
    );
  } catch (error) {
    yield showError();
  }
}

function* updateWordUndoSaga(action: PayloadAction<UpdateWordUndoPayload>) {
  const { id, word, restrictionGroups, selectedGroup } = action.payload;
  try {
    yield all([
      call(updateRestrictedWordRequest, word, id),
      call(addRestrictionGroupsRequest, id, restrictionGroups)
    ]);

    yield put(fetchRestrictedWord({ restrictedWordId: id }));
    yield put(changeSelectedGroup(selectedGroup));

    yield put(
      showToast({
        type: 'success',
        message: 'Changes canceled'
      })
    );

    yield put(fetchRestrictedGroups());
  } catch (error) {
    yield showError();
  }
}

function* fetchGroupArticlesSaga(action: PayloadAction<number>) {
  try {
    const groupId = action.payload;
    const response = yield call(fetchGroupArticlesRequest, groupId);
    if (response) {
      const articles = response;
      yield put(setArticles(articles));
    }
  } catch (error) {
    yield showError();
  }
}

function* deleteGroupSaga(action: PayloadAction<DeleteGroupPayload>) {
  try {
    const { groupId, groupName, deleteWordsFlag, globalGroup } = action.payload;
    const word = {
      groupId,
      deleteWordsFlag
    };
    yield call(deleteGroupRequest, word);
    yield put(refreshRestrictedWordsData());
    yield put(closeGroupSettings());
    yield put(changeSelectedGroup(globalGroup));
    yield put(
      showToast({
        type: 'warning',
        message: deleteWordsFlag
          ? `The ${groupName} deleted and it's individual words deleted from the system`
          : `Group ${groupName} deleted and it’s individual words transferred to globally applicable list`
      })
    );
  } catch (error) {
    yield showError();
  }
}

// The undo functionality is not implemented yet
function deleteGroupUndoSaga(action: PayloadAction<DeleteGroupPayload>) {}

function* updateRestrictedGroupSaga(action: PayloadAction<UpdateGroupPayload>) {
  yield put(setRestrictedWordsIsLoading(true));
  const { groupId, name, previousName, updateGroupSucceededCallback } = action.payload;
  try {
    yield call(updateRestrictedGroupRequest, groupId, name);

    yield put(refreshRestrictedWordsData());
    yield put(
      showToast({
        type: 'success',
        message: 'Group updated',
        onAction: dispatch => {
          dispatch(
            updateRestrictedGroupUndo({
              groupId,
              previousName,
              updateGroupSucceededCallback
            })
          );
        }
      })
    );
  } catch (error) {
    yield showError();
  }
  yield put(setRestrictedWordsIsLoading(false));
}

function* updateRestrictedGroupUndoSaga(action: PayloadAction<UpdateGroupPayload>) {
  const { groupId, previousName, updateGroupSucceededCallback } = action.payload;
  try {
    const updatedGroupResponse = yield call(updateRestrictedGroupRequest, groupId, previousName);
    const { name } = updatedGroupResponse;
    updateGroupSucceededCallback(name);
    yield put(refreshRestrictedWordsData());
    yield put(
      showToast({
        type: 'success',
        message: 'Changes canceled'
      })
    );
  } catch (error) {
    yield showError();
  }
}

function* assignGroupToArticleSaga(action: PayloadAction<AssignGroupToArticlePayload>) {
  yield put(setRestrictedWordsIsLoading(true));
  const {
    postId,
    articleName,
    restrictedGroups,
    undoRestrictedGroups,
    successCallback
  } = action.payload;
  const newAssignedGroupsList = {
    postId,
    articleName,
    restrictedGroups
  };

  const undoAssignedGroupsList = {
    postId,
    articleName,
    restrictedGroups: undoRestrictedGroups
  };
  try {
    yield call(assignGroupToArticleRequest, newAssignedGroupsList);
    yield put(updateArticlesGroups({ postId, restrictedWordsGroups: restrictedGroups }));
    yield put(
      showToast({
        type: 'success',
        message: 'Article updated',
        onAction: dispatch => {
          dispatch(assignGroupToArticleUndo(undoAssignedGroupsList));
        }
      })
    );
    successCallback();
  } catch (error) {
    yield showError();
  }
  yield put(setRestrictedWordsIsLoading(false));
}

function* assignGroupToArticleUndoSaga(action: PayloadAction<AssignGroupToArticlePayload>) {
  const { postId, restrictedGroups } = action.payload;
  try {
    yield call(assignGroupToArticleRequest, action.payload);
    yield put(updateArticlesGroups({ postId, restrictedWordsGroups: restrictedGroups }));
    yield put(
      showToast({
        type: 'success',
        message: 'Changes canceled'
      })
    );
  } catch (error) {
    yield showError();
  }
}

function* fetchRestrictedGroupsSaga() {
  try {
    const groupsResponse: FetchRestrictedGroupsResponse = yield call(fetchRestrictedGroupsRequest);
    const parsedGroups = parseGroups(groupsResponse);
    yield put(setRestrictedGroups(parsedGroups));
  } catch (error) {
    yield showError();
  }
}

export function* restrictedWordsSagas() {
  yield takeLatest(fetchRestrictedWordsInitData, fetchRestrictedWordsInitDataSaga);
  yield takeLatest(updateWordPolicy, updateWordPolicySaga);
  yield takeLatest(changeSelectedGroup, fetchRestrictedWordsByGroupSaga);
  yield takeLatest(addNewWordsToGlobal, addNewWordsToGlobalSaga);
  yield takeLatest(deleteWordsFromGlobal, deleteWordsFromGlobalSaga);
  yield takeLatest(makeWordsGlobal, makeWordsGlobalSaga);
  yield takeLatest(makeWordsGlobalUndo, makeWordsGlobalUndoSaga);
  yield takeLatest(addVariousWordsToGlobal, addVariousWordsToGlobalSaga);
  yield takeLatest(updateRestrictedWord, updateRestrictedWordSaga);
  yield takeLatest(deleteRestrictedWords, deleteRestrictedWordsSaga);
  yield takeLatest(addVariousWordsToGlobalUndo, addVariousWordsToGlobalUndoSaga);
  yield takeLatest(addWordsToGroup, addWordsToGroupSaga);
  yield takeLatest(addWordsToGroupUndo, addWordsToGroupUndoSaga);
  yield takeLatest(deleteWordsFromGroup, deleteWordsFromGroupSaga);
  yield takeLatest(deleteWords, deleteWordsSaga);
  yield takeLatest(refreshRestrictedWordsData, refreshRestrictedWordsDataSaga);
  yield takeLatest(deleteWordUndo, deleteWordUndoSaga);
  yield takeLatest(updateWordUndo, updateWordUndoSaga);
  yield takeLatest(fetchGroupArticles, fetchGroupArticlesSaga);
  yield takeLatest(deleteGroup, deleteGroupSaga);
  yield takeLatest(deleteGroupUndo, deleteGroupUndoSaga);
  yield takeLatest(updateRestrictedGroup, updateRestrictedGroupSaga);
  yield takeLatest(assignGroupToArticle, assignGroupToArticleSaga);
  yield takeLatest(updateRestrictedGroupUndo, updateRestrictedGroupUndoSaga);
  yield takeLatest(fetchRestrictedGroups, fetchRestrictedGroupsSaga);
  yield takeLatest(fetchRestrictedWord, fetchRestrictedWordSaga);
  yield takeLatest(assignGroupToArticleUndo, assignGroupToArticleUndoSaga);
}
